import React from 'react'
import { setStorage, getStorage } from 'src/services/localStorage'
import { useAccountSelect } from './useAccount'
import { useNumbersActions, useNumbersSelect } from './useNumbers'
import { useEmailActions, useEmailSelect } from './useEmail'
import { Drivers } from 'src/services/accounts'
import { IConnectedAccount } from './useAccount'
import { isEqual } from 'lodash'

export enum AccountsType {
  EMAIL = 'EMAIL',
  COMMUNICATION = 'COMMUNICATION',
}

const getAccountsFromStorage = (
  accountsType: AccountsType
): IConnectedAccount[] => {
  const storage = getStorage()

  return storage.accountsConfig?.[accountsType] || []
}

const setAccountsToStorage = (
  accountsType: AccountsType,
  accounts: IConnectedAccount[]
) => {
  const storage = getStorage()
  setStorage({
    accountsConfig: {
      ...storage.accountsConfig,
      [accountsType]: accounts,
    },
  })
}

export const useSelectedAccounts = (accountsType: AccountsType) => {
  const [accounts, setAccounts] = React.useState<
    (IConnectedAccount & { useDefault?: boolean })[] | null
  >(null)

  const user = useAccountSelect.user()

  const numbers = useNumbersSelect.numbers()
  const getUserNumbers = useNumbersActions.getNumbers()

  const userMailAccounts = useEmailSelect.userMailAccounts()
  const getUserMailAccounts = useEmailActions.getUserEmailsAccounts()

  const isEmailType = accountsType === AccountsType.EMAIL
  const sourceData = (
    isEmailType ? userMailAccounts : numbers.data
  ) as IConnectedAccount[]
  const cursor = isEmailType ? null : numbers.cursor

  const sourceDataKey = React.useMemo(
    () =>
      JSON.stringify(
        sourceData.map((acc) => ({ id: acc.id, status: acc.status }))
      ),
    [sourceData]
  )

  const selectedDefaultAccount = React.useMemo(() => {
    return accounts?.find((acc) => acc.useDefault)
  }, [accounts])

  React.useEffect(() => {
    const storageAccounts = getAccountsFromStorage(accountsType)
    setAccounts(storageAccounts)
  }, [accountsType])

  React.useEffect(() => {
    const handleAccountsSync = (event: CustomEvent) => {
      if (event.detail.accountsType === accountsType) {
        const storageAccounts = getAccountsFromStorage(accountsType)
        if (!isEqual(accounts, storageAccounts)) {
          setAccounts(storageAccounts)
        }
      }
    }

    window.addEventListener('accounts-sync' as any, handleAccountsSync as any)

    return () => {
      window.removeEventListener(
        'accounts-sync' as any,
        handleAccountsSync as any
      )
    }
  }, [])

  React.useEffect(() => {
    if (sourceData.length > 0 && accounts !== null) {
      let accountsWithDefault = sourceData.map((acc) => ({
        ...acc,
        useDefault: accounts.find((a) => a.id === acc.id)?.useDefault,
      }))

      const currentDefaultAccount = accountsWithDefault.find(
        (acc) => acc.useDefault
      )
      const isCurrentDefaultInvalid = currentDefaultAccount
        ? isEmailType
          ? currentDefaultAccount.status === 'deactivate'
          : currentDefaultAccount.driver !== Drivers.TWILIO ||
            currentDefaultAccount.status === 'inactive'
        : false

      const hasValidDefaultAccount = accountsWithDefault.some(
        (acc) => acc.useDefault && !isCurrentDefaultInvalid
      )

      if (!hasValidDefaultAccount && accountsWithDefault.length > 0) {
        const firstValidAccountIndex = isEmailType
          ? accountsWithDefault.findIndex((acc) => acc.status !== 'deactivate')
          : accountsWithDefault.findIndex(
              (acc) =>
                acc.driver === Drivers.TWILIO && acc.status !== 'inactive'
            )

        if (firstValidAccountIndex !== -1) {
          accountsWithDefault = accountsWithDefault.map((acc, idx) => ({
            ...acc,
            useDefault: idx === firstValidAccountIndex,
          }))
        } else {
          accountsWithDefault = accountsWithDefault.map((acc) => ({
            ...acc,
            useDefault: false,
          }))
        }
      }

      if (!isEqual(accounts, accountsWithDefault)) {
        setAccounts(accountsWithDefault)
        setAccountsToStorage(accountsType, accountsWithDefault)
      }
    } else {
      if (accounts !== null && accounts.length > 0) {
        setAccounts([])
        setAccountsToStorage(accountsType, [])
      }
    }
  }, [sourceDataKey, accountsType, isEmailType, accounts])

  const initialize = () => {
    if (isEmailType) {
      getUserMailAccounts({
        userId: user?.id,
      })
    } else {
      getUserNumbers({
        id: user?.id,
      })
    }
  }

  const setDefaultAccount = React.useCallback(
    (accountId: number) => {
      if (!accounts) return

      const updatedAccounts = accounts.map((acc) => {
        const isValidAccount = isEmailType
          ? acc.status !== 'deactivate'
          : acc.driver === Drivers.TWILIO && acc.status !== 'inactive'

        return {
          ...acc,
          useDefault: isValidAccount ? acc.id === accountId : false,
        }
      })

      setAccounts(updatedAccounts)
      setAccountsToStorage(accountsType, updatedAccounts)

      window.dispatchEvent(
        new CustomEvent('accounts-sync', {
          detail: { accountsType },
        })
      )
    },
    [accounts, accountsType, isEmailType]
  )

  return {
    accounts: accounts || [],
    selectedDefaultAccount,
    initialize,
    cursor,
    setDefaultAccount,
  }
}
